import { call, put, takeEvery, takeLatest } from 'redux-saga/effects';
import callApi, { abortableCallApi, callDownload } from 'utils/callApi';
import { LOCATION_CHANGE } from 'react-router-redux';

import { fetchAccountInformationSuccess } from 'redux/modules/account';
import { fetchAppInfoSuccess } from 'redux/modules/appInfo';
import { addNotification } from 'redux/modules/notifications';
import store from 'redux/configureStore';

// Actions
export const FETCH_INSURANCES = 'FETCH_INSURANCES';
export const FETCH_INSURANCES_SUCCESS = 'FETCH_INSURANCES_SUCCESS';
export const FETCH_INSURANCES_FAIL = 'FETCH_INSURANCES_FAIL';
export const CLEAR_INSURANCES = 'CLEAR_INSURANCES';
export const CREATE_INSURANCE = 'CREATE_INSURANCE';
export const CREATE_INSURANCE_SUCCESS = 'CREATE_INSURANCE_SUCCESS';
export const CREATE_INSURANCE_FAIL = 'CREATE_INSURANCE_FAIL';
export const UPDATE_INSURANCE = 'UPDATE_INSURANCE';
export const UPDATE_INSURANCE_SUCCESS = 'UPDATE_INSURANCE_SUCCESS';
export const UPDATE_INSURANCE_FAIL = 'UPDATE_INSURANCE_FAIL';
export const DELETE_INSURANCE = 'DELETE_INSURANCE';
export const DELETE_INSURANCE_SUCCESS = 'DELETE_INSURANCE_SUCCESS';
export const DELETE_INSURANCE_FAIL = 'DELETE_INSURANCE_FAIL';
export const UPDATE_INSURANCES_DATA = 'UPDATE_INSURANCES_DATA';
export const UPDATE_INSURANCES_DATA_SUCCESS = 'UPDATE_INSURANCES_DATA_SUCCESS';
export const UPDATE_INSURANCES_DATA_FAIL = 'UPDATE_INSURANCES_DATA_FAIL';

// Reducer
export default function insurancesReducer(state = {
  data: [], count: 0, loading: 0,
}, action) {
  const { type } = action;
  let triggerLoading;
  let payload;
  if (action.payload) {
    ({ triggerLoading, ...payload } = action.payload);
  }
  switch (type) {
    case FETCH_INSURANCES:
      return {
        ...state,
        ...triggerLoading && state.loading <= 0 ? { loading: state.loading + 1 } : {},
      };
    case FETCH_INSURANCES_SUCCESS: {
      if (payload.push) {
        const newData = [
          ...state.data.slice(0, state.data.length),
          ...payload.data.slice(0, payload.data.length),
        ];
        return {
          ...state,
          count: payload.count,
          data: newData,
          ...triggerLoading ? { loading: state.loading - 1 } : {},
        };
      }
      return {
        ...state,
        ...payload,
        ...triggerLoading ? { loading: state.loading - 1 } : {},
      };
    }
    case FETCH_INSURANCES_FAIL:
      return {
        ...state,
        ...triggerLoading ? { loading: state.loading - 1 } : {},
      };
    case CLEAR_INSURANCES:
      return {
        ...state,
        data: [],
        count: 0,
      };
    case CREATE_INSURANCE_SUCCESS: {
      const newData = [
        ...state.data.slice(0, state.data.length),
        payload,
      ];
      return {
        ...state,
        count: state.count + 1,
        data: newData,
      };
    }
    case UPDATE_INSURANCE_SUCCESS: {
      const newData = state.data.map((dataItem) => {
        if (dataItem.ID !== payload.ID) {
          return dataItem;
        }
        return {
          ...dataItem,
          ...payload,
        };
      });
      return {
        ...state,
        data: newData,
      };
    }
    case DELETE_INSURANCE_SUCCESS: {
      const index = state.data.findIndex(dataItem => dataItem.ID === payload.ID);
      const newData = [
        ...state.data.slice(0, index),
        ...state.data.slice(index + 1),
      ];
      return {
        ...state,
        count: state.count - 1,
        data: newData,
      };
    }
    case LOCATION_CHANGE:
      return {
        ...state,
        loading: 0,
      };
    default:
      return state;
  }
}

// Action Creators
export function fetchInsurances(payload) {
  return { type: FETCH_INSURANCES, payload };
}

export function fetchInsurancesSuccess(payload) {
  return { type: FETCH_INSURANCES_SUCCESS, payload };
}

export function fetchInsurancesFail(payload) {
  return { type: FETCH_INSURANCES_FAIL, payload };
}

export function clearInsurances(payload) {
  return { type: CLEAR_INSURANCES, payload };
}

export function createInsurance(payload) {
  return { type: CREATE_INSURANCE, payload };
}

export function createInsuranceSuccess(payload) {
  return { type: CREATE_INSURANCE_SUCCESS, payload };
}

export function createInsuranceFail(payload) {
  return { type: CREATE_INSURANCE_FAIL, payload };
}

export function updateInsurance(payload) {
  return { type: UPDATE_INSURANCE, payload };
}

export function updateInsuranceSuccess(payload) {
  return { type: UPDATE_INSURANCE_SUCCESS, payload };
}

export function updateInsuranceFail(payload) {
  return { type: UPDATE_INSURANCE_FAIL, payload };
}

export function deleteInsurance(payload) {
  return { type: DELETE_INSURANCE, payload };
}

export function deleteInsuranceSuccess(payload) {
  return { type: DELETE_INSURANCE_SUCCESS, payload };
}

export function deleteInsuranceFail(payload) {
  return { type: DELETE_INSURANCE_FAIL, payload };
}

export function updateInsurancesData(payload) {
  return { type: UPDATE_INSURANCES_DATA, payload };
}

export function updateInsurancesDataSuccess(payload) {
  return { type: UPDATE_INSURANCES_DATA_SUCCESS, payload };
}

export function updateInsurancesDataFail(payload) {
  return { type: UPDATE_INSURANCES_DATA_FAIL, payload };
}

// Requests

let abort;

export function requestFetchInsurances({ limit, offset }) {
  if (abort) {
    abort();
    abort = null;
  }
  const request = abortableCallApi(`resource/insurance_info/?limit=${limit}&offset=${offset}`);
  abort = request.abort;
  return request.res;
}

export function requestSearchInsurances({ filters, limit, offset }) {
  if (abort) {
    abort();
    abort = null;
  }
  const request = abortableCallApi('resource/insurance_info/search/', 'POST', {
    filters,
    sort_by: [
      {
        name: 'Payer Name',
        sort: 'asc',
      },
    ],
    limit,
    offset,
  });
  abort = request.abort;
  return request.res;
}

export function requestCreateInsurance(payload) {
  return callApi('resource/insurance_info/', 'POST', payload);
}

export function requestUpdateInsurance(payload) {
  return callApi('resource/insurance_info/', 'PATCH', payload);
}

export function requestDeleteInsurance(payload) {
  return callApi(`resource/insurance_info/?id=${payload}`, 'DELETE');
}

export function requestUpdateInsurancesData() {
  return callApi('claim_md/update_payers/');
}

// Sagas
function* fetchInsurancesSaga(action) {
  const { triggerLoading, filters } = action.payload;
  try {
    const res = filters
      ? yield call(requestSearchInsurances, action.payload)
      : yield call(requestFetchInsurances, action.payload);
    yield put(fetchInsurancesSuccess({
      data: res.data,
      count: res.count,
      triggerLoading,
      ...action.payload.push ? { push: true } : {},
    }));
    yield put(fetchAccountInformationSuccess(res.user_info));
    yield put(fetchAppInfoSuccess(res.app_info));
  } catch (err) {
    yield put(fetchInsurancesFail({
      ...err.data,
      triggerLoading,
    }));
  }
}

function* createInsuranceSaga(action) {
  const {
    values,
    resolve,
    reject,
  } = action.payload;
  try {
    const res = yield call(requestCreateInsurance, values);
    yield put(createInsuranceSuccess(res.data));
    yield put(fetchAccountInformationSuccess(res.user_info));
    yield put(fetchAppInfoSuccess(res.app_info));
    yield put(addNotification({
      message: 'New Payer successfully added',
      variant: 'success',
    }));
    if (resolve) {
      resolve(res.data);
    }
  } catch (err) {
    yield put(createInsuranceFail(err.data));
    if (reject) {
      reject(err.data);
    }
  }
}

function* updateInsuranceSaga(action) {
  const {
    values,
    resolve,
    reject,
  } = action.payload;
  try {
    const res = yield call(requestUpdateInsurance, values);
    yield put(updateInsuranceSuccess(res.data));
    yield put(fetchAccountInformationSuccess(res.user_info));
    yield put(fetchAppInfoSuccess(res.app_info));
    yield put(addNotification({
      message: 'Payer successfully updated',
      variant: 'success',
    }));
    if (resolve) {
      resolve(res.data);
    }
  } catch (err) {
    yield put(updateInsuranceFail(err.data));
    if (reject) {
      reject(err.data);
    }
  }
}

function* deleteInsuranceSaga(action) {
  const { ID } = action.payload;
  try {
    const res = yield call(requestDeleteInsurance, ID);
    yield put(deleteInsuranceSuccess({ ID }));
    yield put(fetchAccountInformationSuccess(res.user_info));
    yield put(fetchAppInfoSuccess(res.app_info));
    yield put(addNotification({
      message: 'Payer successfully deleted',
      variant: 'success',
    }));
  } catch (err) {
    yield put(deleteInsuranceFail(err.data));
    yield put(addNotification({
      message: 'Failed to delete Payer',
      variant: 'warning',
    }));
  }
}

function* updateInsurancesDataSaga(action) {
  const {
    resolve,
    reject,
  } = action.payload;
  try {
    const res = yield call(requestUpdateInsurancesData);
    yield put(fetchAccountInformationSuccess(res.user_info));
    yield put(fetchAppInfoSuccess(res.app_info));
    yield put(updateInsurancesDataSuccess());
    const { Result, ID  } = res.data;
    yield put(addNotification({
      message: `${Result.pass} Payers successfully processed. ${Result.create} created, ${Result.update} updated, ${Result.delete} deleted.`,
      variant: 'success',
    }));
    if (resolve) {
      resolve(res.data);
    }
  } catch (err) {
    const { ID } = err.data;
    yield put(updateInsurancesDataFail(err.data));
    yield put(addNotification({
      message: 'Failed to update Payers',
      variant: 'warning',
      actionText: 'Download Explain',
      onActionClick: () => {
        callDownload(`claim_md/update_payers/explain/?ID=${ID}&LEVEL=full`, 'GET', `${ID}-payers-data-update-explain`);
      },
    }));
    if (reject) {
      reject(err.data);
    }
  }
}

export function* insurancesSaga() {
  yield takeLatest(FETCH_INSURANCES, fetchInsurancesSaga);
  yield takeEvery(CREATE_INSURANCE, createInsuranceSaga);
  yield takeEvery(UPDATE_INSURANCE, updateInsuranceSaga);
  yield takeEvery(DELETE_INSURANCE, deleteInsuranceSaga);
  yield takeEvery(UPDATE_INSURANCES_DATA, updateInsurancesDataSaga);
}



// WEBPACK FOOTER //
// ./src/redux/modules/insurances.js